import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import emptyTableImage from 'icons/vendors/emptyTableImage.png';
import {
  FormationsGhostButton,
  FormationsTertiaryButton,
} from 'components/common/Buttons';
import { AddIcon } from 'components/common/Icon';
import {
  useCurrentUser,
  UserIdentity,
  useUpdateAccountProgress,
  useUserIdentityById,
  useCreateOwner,
  useOwners,
} from 'hooks/api';
import {
  IAccount,
  ProgressTrackerGroupRequest,
  ProgressTrackerGroups,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'models/account';
import { Company, CreateOwnerData } from 'models/company';
import { UserInfo } from 'services/users';
import { queryClient } from 'states/reactQueryClient';
import { AlertError } from 'components/FormationsAlert';
import { OwnershipCreateDialog } from 'components/ProgressTracker/Incorporation/OwnershipFormDialog';
import { useMediaBreakpoints } from 'hooks';
import { MobileOwnershipDetails } from 'components/common/tables/MobileOwnershipDetails';
import { ArrowBack } from '@mui/icons-material';
import { SubtitleText } from 'components/ProgressTracker/StyledTypography';
import {
  DeleteOwner,
  EditOwner,
  EditOwnershipPercentage,
  ReadOnlyTable,
} from './helpers';
import { isProgressTrackerStageCompleted } from './state';

interface OwnershipDetailsProps {
  handleComplete: () => void;
  isUpdatingAccountProgress: boolean;
  stageCompleted: boolean;
  companyData: Company;
  accountData: IAccount;
  userData: UserInfo | undefined;
  userIdentity: UserIdentity | undefined;
  onBackClick?: () => void;
  onContinueToAccountVerification: () => void;
}

export const OwnershipDetails = ({
  handleComplete,
  isUpdatingAccountProgress,
  stageCompleted,
  companyData,
  accountData,
  userData,
  userIdentity,
  onBackClick,
  onContinueToAccountVerification,
}: OwnershipDetailsProps) => {
  const [openAddAnotherOwnerPopup, setOpenAddAnotherOwnerPopup] =
    useState(false);
  const [showOwnershipPercentageWarning, setShowOwnershipPercentageWarning] =
    useState(false);

  const { isMobile } = useMediaBreakpoints();

  const companyId = companyData.id;
  const { owners } = useOwners(companyId);

  const { mutate: createOwner } = useCreateOwner({
    onSuccess: () =>
      queryClient.invalidateQueries(['companyOwners', companyId]),
  });

  // call createOwner to add the first owner to the list if the first owner hasn't been added yet
  useEffect(() => {
    if (Array.isArray(owners) && owners.length === 0) {
      const firstOwnerData: CreateOwnerData = {
        companyId: companyData.id ?? '',
        accountId: accountData.id,
        isPrimary: true,
        name: {
          First: userData?.name?.first ?? '',
          Middle: userData?.name?.middle,
          Last: userData?.name?.last ?? '',
        },
        ssn: `${userIdentity?.ssn?.first5}${userIdentity?.ssn?.last4}`,
        dob: userData?.dob,
        email: userData?.email,
        ownershipPercentage: 100,
      };
      createOwner({ companyId, data: firstOwnerData });
    }
  }, [owners]);

  const ownershipPercentageList = useMemo(() => {
    let ownerArray: number[] = [100];
    if (owners) {
      ownerArray = owners
        .map((owner) => owner.ownershipPercentage)
        .filter((percentage): percentage is number => percentage !== undefined);
    }
    return ownerArray;
  }, [owners]);

  useEffect(() => {
    // if the sum of ownershipPercentageList does not equal 100 then set the warning
    const sum = ownershipPercentageList.reduce(
      (accumulator, currentValue) => accumulator + currentValue,
      0,
    );
    if (sum !== 100 && owners && owners.length > 0) {
      setShowOwnershipPercentageWarning(true);
    } else {
      setShowOwnershipPercentageWarning(false);
    }
  }, [ownershipPercentageList]);

  const handleSaveAndContinue = () => {
    handleComplete();
    if (
      companyData?.entityType !== 'Sole-Prop' &&
      accountData?.formationsPlan !== 'BIB'
    ) {
      onContinueToAccountVerification();
    }
  };

  const renderOwnersSection = () => {
    if (isMobile) {
      return null;
    }

    if (stageCompleted && owners) {
      return <ReadOnlyTable owners={owners} />;
    }

    if (!owners || owners.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'start',
            alignItems: 'start',
          }}
        >
          {!stageCompleted && (
            <FormationsTertiaryButton
              sx={{ mb: 3 }}
              size="large"
              startIcon={<AddIcon sx={{ pb: 0.25 }} />}
              onClick={() => setOpenAddAnotherOwnerPopup(true)}
            >
              Add an Owner
            </FormationsTertiaryButton>
          )}

          <Box
            sx={{
              display: 'flex',
              width: '50%',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <img
              src={emptyTableImage}
              alt="empty ownership table"
              data-testid="empty-table-img"
              style={{ marginBottom: '33px', width: '100%' }}
            />
            <Typography variant="body3S" align="center">
              No ownership information has been entered yet
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left" style={{ width: '30%' }}>
                Name
              </TableCell>
              <TableCell align="left" style={{ width: '20%' }}>
                Date of Birth
              </TableCell>
              <TableCell align="left" style={{ width: '25%' }}>
                Social Security
              </TableCell>
              <TableCell align="left" style={{ width: '20%' }}>
                Ownership
              </TableCell>
              <TableCell style={{ width: '2.5%' }} />
              <TableCell style={{ width: '2.5%' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {owners.map((owner) => (
              <TableRow key={owner.id}>
                <TableCell style={{ width: '30%' }}>
                  {owner.name?.First} {owner.name?.Last}
                </TableCell>
                <TableCell style={{ width: '20%' }}>
                  {owner.dob ? `${owner.dob}` : 'N/A'}
                </TableCell>
                <TableCell style={{ width: '25%' }}>***-**-****</TableCell>
                <TableCell style={{ width: '20%' }}>
                  <EditOwnershipPercentage
                    owner={owner}
                    hasError={showOwnershipPercentageWarning}
                  />
                </TableCell>
                <TableCell style={{ width: '2.5%' }}>
                  <EditOwner
                    ownerId={owner.id}
                    companyId={owner.companyId}
                    accountId={owner.accountId}
                    isPrimary={owner.isPrimary}
                  />
                </TableCell>
                <TableCell style={{ width: '2.5%' }}>
                  <DeleteOwner owner={owner} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {showOwnershipPercentageWarning && (
          <Grid item xs={12} sx={{ mt: 3 }}>
            <AlertError text="Total ownership must equal 100%. Please review the ownership percentage before you can continue." />
          </Grid>
        )}

        {!stageCompleted && (
          <Grid item xs={12}>
            <FormationsTertiaryButton
              sx={{ mt: 3 }}
              size="large"
              startIcon={<AddIcon sx={{ pb: 0.25 }} />}
              onClick={() => setOpenAddAnotherOwnerPopup(true)}
            >
              Add an Owner
            </FormationsTertiaryButton>
          </Grid>
        )}
      </>
    );
  };

  return (
    <Grid container spacing={1} style={{ maxWidth: '100%' }}>
      <Grid item xs={12}>
        <Typography variant="h5B" component="span">
          Ownership Details
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <SubtitleText variant="body1">
          Add owner information
        </SubtitleText>
      </Grid>
      {owners && owners.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h7B" color="text.primary">
            Ownership
          </Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        {isMobile &&
          (stageCompleted && owners ? (
            <MobileOwnershipDetails companyId={companyId} allowEdit={false} />
          ) : (
            <MobileOwnershipDetails companyId={companyId} allowEdit />
          ))}
        {renderOwnersSection()}
      </Grid>
      {!stageCompleted && (
        <Grid
          item
          xs={12}
          container
          marginTop={4}
          justifyContent="space-between"
        >
          {onBackClick && (
            <FormationsGhostButton
              type="button"
              size="large"
              startIcon={<ArrowBack />}
              onClick={onBackClick}
              disabled={isUpdatingAccountProgress}
            >
              Back
            </FormationsGhostButton>
          )}

          <FormationsTertiaryButton
            isLoading={isUpdatingAccountProgress}
            onClick={handleSaveAndContinue}
            size="large"
            data-testid="save-ownership-details"
            loadingPosition="end"
            disabled={showOwnershipPercentageWarning || owners?.length === 1}
          >
            Next
          </FormationsTertiaryButton>
        </Grid>
      )}
      {openAddAnotherOwnerPopup && (
        <OwnershipCreateDialog
          accountId={accountData.id}
          companyId={companyData.id ?? ''}
          open={openAddAnotherOwnerPopup}
          onClose={() => setOpenAddAnotherOwnerPopup(false)}
        />
      )}
    </Grid>
  );
};

interface OwnershipDetailsContainerProps {
  company: Company;
  account: IAccount;
  onBackClick?: () => void;
  onContinueToAccountVerification: () => void;
}

export const OwnershipDetailsContainer = ({
  company,
  account,
  onBackClick,
  onContinueToAccountVerification,
}: OwnershipDetailsContainerProps) => {
  const {
    mutateAsync: updateAccountProgress,
    isLoading: isUpdatingAccountProgress,
  } = useUpdateAccountProgress(account.id, {
    onSuccess: () => queryClient.invalidateQueries(['progressTracker']),
  });

  const handleComplete = async () => {
    const requestProps: ProgressTrackerGroupRequest = {
      group: ProgressTrackerGroups.Incorporation,
      status: ProgressTrackerStatus.Completed,
      stage: ProgressTrackerStages.OwnershipDetails,
    };

    await updateAccountProgress({
      progress: [requestProps],
      eventData: {
        stage: ProgressTrackerStages.OwnershipDetails,
        stageStatus: ProgressTrackerStatus.Completed,
        entityType: company?.entityType ?? company?.legacyEntityType ?? '',
      },
    });
  };

  const stagedCompleted = isProgressTrackerStageCompleted(
    account,
    ProgressTrackerStages.OwnershipDetails,
  );

  const { currentUser: user } = useCurrentUser();
  const { userIdentity } = useUserIdentityById(user?.id || '', {
    enabled: !!user?.id,
  });

  return (
    <OwnershipDetails
      handleComplete={handleComplete}
      isUpdatingAccountProgress={isUpdatingAccountProgress}
      stageCompleted={stagedCompleted}
      companyData={company}
      accountData={account}
      userData={user}
      userIdentity={userIdentity}
      onBackClick={onBackClick}
      onContinueToAccountVerification={onContinueToAccountVerification}
    />
  );
};
